import { FC, KeyboardEvent, useEffect, useRef, useState } from 'react'
import dynamic from 'next/dynamic'
import Image from 'next/image'
import clsx from 'clsx'

import ScrollArea from 'containers/scroll-area/scroll-area'
const UserName = dynamic(() => import('../messages/chat-message/user-name'))
import useDeviceOrientation, { ScreenOrientation } from 'hooks/useDeviceOrientation'

import { IChatMessage } from 'constants/types/webinar.types'
import { STATIC_URL } from 'constants/data'
import { buttonIdWebinarStarted } from 'constants/clickableElementsId'

import styles from './chat-bottom.module.scss'

// const dropDownURL = `url(${STATIC_URL}/navbar/dropdown_blue.svg)`
const dropDownURL = `${STATIC_URL}/navbar/dropdown_blue.svg`

const iconClose = `${STATIC_URL}/filters/icon_close_black.svg`

interface IChatInput {
  handleWebSocketSend: (message: string, id?: string) => void
  msgReplyTo: IChatMessage | null
  handleCancelReply: () => void
  handleCancelInput: () => void
}

const ChatInput: FC<IChatInput> = ({
  handleWebSocketSend,
  msgReplyTo,
  handleCancelReply,
  handleCancelInput,
}: IChatInput) => {
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const orientation = useDeviceOrientation()

  const [inputValue, setInputValue] = useState('')
  const [fullNameValue, setFullNameValue] = useState('')

  const isHorizontalOrientation = orientation === ScreenOrientation.horizontal
  const msgReplyId = msgReplyTo?._id

  const getFullName = () => {
    if (msgReplyTo) {
      return [
        msgReplyTo.author?.firstName,
        msgReplyTo.author?.secondName,
        msgReplyTo.author?.lastName,
      ]
        .filter(Boolean)
        .join(' ')
    }
    return ''
  }

  const handleCancel = () => {
    handleCancelReply()
  }

  const handleSend = () => {
    if (!inputValue) return
    if (msgReplyId && inputValue.length <= fullNameValue.length) return
    if (inputValue?.trim && inputValue.trim()) {
      const inputText = inputValue.trim()
      handleWebSocketSend(inputText, msgReplyId)
      handleCancelInput()
    }
  }

  const handleKeyPress = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    e.stopPropagation()
    if (e.key === 'Enter' || e.keyCode === 13) {
      e.preventDefault()
      handleSend()
    }
  }

  useEffect(() => {
    if (msgReplyId) {
      inputRef?.current?.focus()
    }
  }, [msgReplyId])

  useEffect(() => {
    const textarea = inputRef?.current
    textarea &&
      textarea.addEventListener('input', () => {
        if (textarea.scrollHeight > 40) {
          textarea.style.height = 'auto'
          textarea.style.height = textarea.scrollHeight + 'px'
        }
      })
  }, [])

  useEffect(() => {
    if (msgReplyId) {
      const fullName = getFullName()
      setFullNameValue(`${fullName}, `)
      setInputValue(`${getFullName()}, `)
    } else {
      setFullNameValue('')
    }
  }, [msgReplyId])

  return (
    <>
      {msgReplyId && (
        <div className={styles.replyMsgWrapper}>
          <p className={styles.answer}>
            В ответ{' '}
            <UserName
              firstName={msgReplyTo.author?.firstName}
              secondName={msgReplyTo.author?.secondName}
              lastName={msgReplyTo.author?.lastName}
              isAdminMessage={Boolean(msgReplyTo.author?.isAdmin)}
            />
          </p>
          <p className={styles.replyMsgFragment}>
            <span className={styles.replyTxt}>{msgReplyTo.text}</span>
          </p>
          <Image
            className={styles.close}
            src={iconClose}
            width="10"
            height="10"
            alt="Закрыть"
            onClick={handleCancel}
          />
        </div>
      )}
      <ScrollArea>
        <div
          className={clsx(styles.chatInputBlock, {
            [styles.inputHorizontal]: isHorizontalOrientation,
            [styles.noTopMargin]: msgReplyId,
            [styles.chatInputBlockNew]: true,
          })}
        >
          <textarea
            className={styles.input}
            onKeyDown={handleKeyPress}
            value={inputValue}
            role="textbox"
            ref={inputRef}
            onChange={(el) => {
              if (!el.target.value.startsWith(fullNameValue)) {
                setInputValue(fullNameValue)
              } else {
                setInputValue(el.target.value)
              }
            }}
            placeholder="Введите сообщение..."
          />
          <button
            className={clsx(styles.inputButton, {
              [styles.inputButtonNew]: true,
              [styles.inputButtonActive]: msgReplyId
                ? inputValue.length > fullNameValue.length
                : inputValue.length > 0,
            })}
            id={buttonIdWebinarStarted}
            onClick={handleSend}
          >
            <Image
              src={dropDownURL}
              className={clsx(styles.basicIcon, {
                [styles.activeIcon]: msgReplyId
                  ? inputValue.length > fullNameValue.length
                  : inputValue.length > 0,
              })}
              width="14"
              height="15"
              alt="first calendar icon"
            />
          </button>
        </div>
      </ScrollArea>
    </>
  )
}

export default ChatInput
